/**
 * The required fields an *artist* can actually fill in from an invite link, in one
 * place so the two invite forms and the combined page can't drift apart.
 *
 * Deliberately narrower than the DAL's `summarizePerson` / `summarizeHappening`:
 * those also track admin-side fields (episode number, artists, start time, duration)
 * that no artist-facing form exposes, so gating a submit on them would leave the
 * artist staring at a blocker they have no way to clear.
 */

import type { Happening, Person } from '@littlebooker/dal';

export interface ArtistRequiredValues {
  name: string;
  email: string;
  phone: string;
  bio: string;
  pictureUrl: string;
}

export interface ShowRequiredValues {
  title: string;
  description: string;
  imageUrl: string;
  isResidentShow: boolean;
}

/** Field key -> the label an artist sees in the "still missing" list. */
export const ARTIST_FIELD_LABELS: Record<keyof ArtistRequiredValues, string> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  bio: 'Bio',
  pictureUrl: 'Profile picture',
};

export const SHOW_FIELD_LABELS: Record<keyof Omit<ShowRequiredValues, 'isResidentShow'>, string> = {
  title: 'Show title',
  description: 'Description',
  imageUrl: 'Show photo',
};

const blank = (value: string | null | undefined) => !value || !value.trim();

/** Required artist-profile fields still empty, as field keys. */
export const missingArtistFields = (values: ArtistRequiredValues): (keyof ArtistRequiredValues)[] =>
  (Object.keys(ARTIST_FIELD_LABELS) as (keyof ArtistRequiredValues)[]).filter((key) => blank(values[key]));

/** Required show fields still empty, as field keys. Title is resident-shows-only. */
export const missingShowFields = (
  values: ShowRequiredValues,
): (keyof Omit<ShowRequiredValues, 'isResidentShow'>)[] => {
  const missing: (keyof Omit<ShowRequiredValues, 'isResidentShow'>)[] = [];
  if (blank(values.description)) missing.push('description');
  if (values.isResidentShow && blank(values.title)) missing.push('title');
  if (blank(values.imageUrl)) missing.push('imageUrl');
  return missing;
};

/** Same rules, applied to a saved record rather than to in-progress form state. */
export const missingArtistFieldsOnPerson = (person: Person): (keyof ArtistRequiredValues)[] =>
  missingArtistFields({
    name: person.name ?? '',
    email: person.email ?? '',
    phone: person.phone ?? '',
    bio: person.bio ?? '',
    pictureUrl: person.picture_url ?? '',
  });

export const missingShowFieldsOnHappening = (
  happening: Happening,
): (keyof Omit<ShowRequiredValues, 'isResidentShow'>)[] =>
  missingShowFields({
    title: happening.title ?? '',
    description: happening.description ?? '',
    imageUrl: happening.image_url ?? '',
    isResidentShow: happening.metadata?.show_type === 'resident',
  });

/** What a form continuously reports to a host page so it can nudge accordingly. */
export interface InviteFormStatus {
  /** Human labels of required fields still empty, right now. */
  missing: string[];
  /** Edits typed since the last successful save. */
  dirty: boolean;
}

export const sameStatus = (a: InviteFormStatus, b: InviteFormStatus) =>
  a.dirty === b.dirty && a.missing.length === b.missing.length && a.missing.every((f, i) => f === b.missing[i]);

/**
 * Imperative handle both invite forms expose so a host page (the combined invite)
 * can validate and save them from a single submit button of its own.
 */
export interface InviteFormHandle {
  /** Surfaces inline errors and returns the human labels still missing. */
  validate: () => string[];
  /** Validates, then saves only if valid. Returns false when validation blocked it. */
  save: () => boolean;
}
